import { supabase } from "@/integrations/supabase/client";
import {
  StaffAttendance,
  StaffShift,
  AttendanceType,
} from "@/models/attendanceModel";
import { format } from "date-fns";
import { logAttendanceEvent } from "@/services/activity/activityLogService";
import { calculateDistance } from "@/services/security/orderSecurityService";
import {
  sendClockInNotification,
  sendAbsenceNotification,
} from "./attendanceNotificationService";

// Add this interface
interface LocationCheck {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

// Add this function to verify staff location
async function verifyStaffLocation(
  storeId: string,
  userLocation: LocationCheck
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get store location from database
    const { data: store, error: storeError } = await supabase
      .from("stores")
      .select("name, latitude, longitude, order_radius_meters")
      .eq("id", storeId)
      .single();

    if (storeError || !store) {
      console.error("Error fetching store location:", storeError);
      return { success: false, error: "Store location verification failed" };
    }

    // If store doesn't have location set
    if (!store.latitude || !store.longitude) {
      console.warn("Store location not set, skipping proximity check");
      return { success: true };
    }

    // Calculate distance between staff and store
    const distance = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      store.latitude,
      store.longitude
    );

    const maxDistance = store.order_radius_meters || 100; // Default 100m radius for staff

    if (distance > maxDistance) {
      return {
        success: false,
        error: `You need to be within ${
          store.name
        } premises to clock in to work. Current distance: ${Math.round(
          distance
        )}m`,
      };
    }

    // Additional check for location accuracy
    if (userLocation.accuracy && userLocation.accuracy > 100) {
      return {
        success: false,
        error:
          "Location accuracy is too low. Please ensure GPS is enabled and try again.",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Location verification error:", error);
    return { success: false, error: "Location verification failed" };
  }
}

// Fetch attendance records for a staff member or all staff
export const fetchAttendanceRecords = async (
  staffId?: string,
  storeId?: string,
  startDate?: Date,
  endDate?: Date
): Promise<StaffAttendance[]> => {
  let query = supabase.from("staff_attendance").select("*");

  if (staffId) {
    query = query.eq("staff_id", staffId);
  }

  if (storeId) {
    query = query.eq("store_id", storeId);
  }

  if (startDate) {
    query = query.gte("clock_in", startDate.toISOString());
  }

  if (endDate) {
    query = query.lte("clock_in", endDate.toISOString());
  }

  const { data, error } = await query.order("clock_in", { ascending: false });

  if (error) {
    console.error("Error fetching attendance records:", error);
    throw error;
  }

  return data.map((record) => ({
    ...record,
    clock_in: record.clock_in ? new Date(record.clock_in) : null,
    clock_out: record.clock_out ? new Date(record.clock_out) : null,
    leave_from: record.leave_from,
    back_on: record.back_on,
    created_at: new Date(record.created_at),
    updated_at: new Date(record.updated_at),
  }));
};

// Clock in a staff member
export const clockIn = async (
  staffId: string,
  storeId: string,
  type: AttendanceType = "work",
  description?: string
): Promise<StaffAttendance> => {
  // Only check location for work-related clock-ins
  if (type === "work" || type === "overtime") {
    try {
      // Get current location
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        }
      );

      const locationCheck = await verifyStaffLocation(storeId, {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });

      if (!locationCheck.success) {
        throw new Error(locationCheck.error);
      }
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Location verification failed"
      );
    }
  }

  const now = new Date();

  const { data, error } = await supabase
    .from("staff_attendance")
    .insert({
      staff_id: staffId,
      store_id: storeId,
      type,
      clock_in: now.toISOString(),
      description,
    })
    .select()
    .single();

  if (error) {
    console.error("Error clocking in:", error);
    throw error;
  }

  // Log the activity
  await logAttendanceEvent(staffId, storeId, "clock_in", {
    type,
    timestamp: now.toISOString(),
  });

  // Send notification to manager for work clock-ins
  if (type === "work" || type === "overtime") {
    try {
      // Get staff and store details for notification
      const [staffResult, storeResult] = await Promise.all([
        supabase
          .from("store_employees")
          .select("first_name, last_name")
          .eq("id", staffId)
          .single(),
        supabase.from("stores").select("name").eq("id", storeId).single(),
      ]);

      if (staffResult.data && storeResult.data) {
        const staffName = `${staffResult.data.first_name} ${staffResult.data.last_name}`;
        const storeName = storeResult.data.name;

        await sendClockInNotification(
          staffId,
          storeId,
          staffName,
          storeName,
          now
        );
      }
    } catch (notificationError) {
      console.error("Error sending clock-in notification:", notificationError);
      // Don't fail the clock-in if notification fails
    }
  }

  return {
    ...data,
    clock_in: data.clock_in ? new Date(data.clock_in) : null,
    clock_out: data.clock_out ? new Date(data.clock_out) : null,
    created_at: new Date(data.created_at),
    updated_at: new Date(data.updated_at),
  };
};

// Clock out a staff member
export const clockOut = async (
  attendanceId: string,
  description?: string
): Promise<StaffAttendance> => {
  const now = new Date();

  // First, get the attendance record to extract staff_id and store_id for logging
  const { data: attendanceData, error: fetchError } = await supabase
    .from("staff_attendance")
    .select("staff_id, store_id, type")
    .eq("id", attendanceId)
    .single();

  if (fetchError) {
    console.error("Error fetching attendance record:", fetchError);
    throw fetchError;
  }

  // Then update the record with clock_out time
  const { data, error } = await supabase
    .from("staff_attendance")
    .update({
      clock_out: now.toISOString(),
      description: description,
    })
    .eq("id", attendanceId)
    .select()
    .single();

  if (error) {
    console.error("Error clocking out:", error);
    throw error;
  }

  // Log the activity
  await logAttendanceEvent(
    attendanceData.staff_id,
    attendanceData.store_id,
    "clock_out",
    {
      type: attendanceData.type,
      timestamp: now.toISOString(),
      total_hours: data.total_hours,
    }
  );

  return {
    ...data,
    clock_in: data.clock_in ? new Date(data.clock_in) : null,
    clock_out: data.clock_out ? new Date(data.clock_out) : null,
    created_at: new Date(data.created_at),
    updated_at: new Date(data.updated_at),
  };
};

// Create an absence record (holiday, sick, etc.)
export const createAbsenceRecord = async (
  staffId: string,
  storeId: string,
  type: AttendanceType,
  startDate: Date,
  endDate: Date,
  description?: string
): Promise<StaffAttendance> => {
  console.log("leaveFrom......", startDate);
  console.log("backOn.........", endDate);
  const { data, error } = await supabase
    .from("staff_attendance")
    .insert({
      staff_id: staffId,
      store_id: storeId,
      type,
      leave_from: startDate.toISOString().split("T")[0], // Store as YYYY-MM-DD
      back_on: endDate.toISOString().split("T")[0], // Store as YYYY-MM-DD
      description,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating absence record:", error);
    throw error;
  }

  // Log the activity
  await logAttendanceEvent(staffId, storeId, "absence_created", {
    type,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    description,
  });

  // Send notification to manager for all absence types
  try {
    // Get staff and store details for notification
    const [staffResult, storeResult] = await Promise.all([
      supabase
        .from("store_employees")
        .select("first_name, last_name")
        .eq("id", staffId)
        .single(),
      supabase.from("stores").select("name").eq("id", storeId).single(),
    ]);

    if (staffResult.data && storeResult.data) {
      const staffName = `${staffResult.data.first_name} ${staffResult.data.last_name}`;
      const storeName = storeResult.data.name;

      await sendAbsenceNotification(
        staffId,
        storeId,
        staffName,
        storeName,
        type,
        startDate,
        endDate,
        description
      );
    }
  } catch (notificationError) {
    console.error("Error sending absence notification:", notificationError);
    // Don't fail the absence creation if notification fails
  }

  return {
    ...data,
    clock_in: null,
    clock_out: null,
    leave_from: data.leave_from,
    back_on: data.back_on,
    created_at: new Date(data.created_at),
    updated_at: new Date(data.updated_at),
  };
};

// Get current active attendance record for a staff member
export const getCurrentAttendanceRecord = async (
  staffId: string
): Promise<StaffAttendance | null> => {
  const { data, error } = await supabase
    .from("staff_attendance")
    .select("*")
    .eq("staff_id", staffId)
    .is("clock_out", null)
    .maybeSingle();

  if (error) {
    console.error("Error fetching current attendance record:", error);
    throw error;
  }

  if (!data) {
    return null;
  }

  return {
    ...data,
    clock_in: data.clock_in ? new Date(data.clock_in) : null,
    clock_out: data.clock_out ? new Date(data.clock_out) : null,
    created_at: new Date(data.created_at),
    updated_at: new Date(data.updated_at),
  };
};

// Get attendance summary for a specific month
export const getMonthlyAttendanceSummary = async (
  staffId: string,
  year: number,
  month: number
): Promise<{
  totalHours: number;
  records: StaffAttendance[];
  byType: Record<AttendanceType, number>;
}> => {
  // Create date range for the month
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59);

  const { data, error } = await supabase
    .from("staff_attendance")
    .select("*")
    .eq("staff_id", staffId)
    .gte("clock_in", startDate.toISOString())
    .lte("clock_in", endDate.toISOString());

  if (error) {
    console.error("Error fetching monthly attendance summary:", error);
    throw error;
  }

  const records = data.map((record) => ({
    ...record,
    clock_in: record.clock_in ? new Date(record.clock_in) : null,
    clock_out: record.clock_out ? new Date(record.clock_out) : null,
    leave_from: record.leave_from,
    back_on: record.back_on,
    created_at: new Date(record.created_at),
    updated_at: new Date(record.updated_at),
  }));

  // Calculate total hours and breakdown by type
  let totalHours = 0;
  const byType: Record<AttendanceType, number> = {
    work: 0,
    overtime: 0,
    holiday: 0,
    sick: 0,
    other: 0,
  };

  records.forEach((record) => {
    if (record.total_hours) {
      totalHours += record.total_hours;
      byType[record.type] += record.total_hours;
    }
  });

  return {
    totalHours,
    records,
    byType,
  };
};

// Shift management
export const fetchStaffShifts = async (
  staffId: string,
  storeId?: string
): Promise<StaffShift[]> => {
  let query = supabase.from("staff_shifts").select("*");

  query = query.eq("staff_id", staffId);

  if (storeId) {
    query = query.eq("store_id", storeId);
  }

  const { data, error } = await query.order("day_of_week");

  if (error) {
    console.error("Error fetching staff shifts:", error);
    throw error;
  }

  return data.map((shift) => ({
    ...shift,
    created_at: new Date(shift.created_at),
    updated_at: new Date(shift.updated_at),
  }));
};

export const createOrUpdateShift = async (
  shift: Omit<StaffShift, "id" | "created_at" | "updated_at">
): Promise<StaffShift> => {
  // Check if a shift already exists for this day
  const { data: existingShift, error: fetchError } = await supabase
    .from("staff_shifts")
    .select("id")
    .eq("staff_id", shift.staff_id)
    .eq("day_of_week", shift.day_of_week)
    .maybeSingle();

  if (fetchError) {
    console.error("Error checking for existing shift:", fetchError);
    throw fetchError;
  }

  let result;

  if (existingShift) {
    // Update existing shift
    const { data, error } = await supabase
      .from("staff_shifts")
      .update({
        start_time: shift.start_time,
        end_time: shift.end_time,
      })
      .eq("id", existingShift.id)
      .select()
      .single();

    if (error) {
      console.error("Error updating shift:", error);
      throw error;
    }

    result = data;
  } else {
    // Create new shift
    const { data, error } = await supabase
      .from("staff_shifts")
      .insert(shift)
      .select()
      .single();

    if (error) {
      console.error("Error creating shift:", error);
      throw error;
    }

    result = data;
  }

  return {
    ...result,
    created_at: new Date(result.created_at),
    updated_at: new Date(result.updated_at),
  };
};

export const deleteShift = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from("staff_shifts").delete().eq("id", id);

  if (error) {
    console.error("Error deleting shift:", error);
    throw error;
  }

  return true;
};

// Get the current shift for a staff member
export const getCurrentShift = async (
  staffId: string
): Promise<StaffShift | null> => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday

  const { data, error } = await supabase
    .from("staff_shifts")
    .select("*")
    .eq("staff_id", staffId)
    .eq("day_of_week", dayOfWeek)
    .maybeSingle();

  if (error) {
    console.error("Error fetching current shift:", error);
    throw error;
  }

  if (!data) {
    return null;
  }

  return {
    ...data,
    created_at: new Date(data.created_at),
    updated_at: new Date(data.updated_at),
  };
};
